import { ReactNode, useState } from 'react';
import { motion } from 'motion/react';
import {
  Home, MapPin, BarChart3, Settings as SettingsIcon,
  Bell, Mic, User, Plus, Sparkles, Map as MapIcon, Zap, Copy, Moon, Sun, Navigation, Ticket,
  ChevronDown, LogOut, Settings, Wifi, WifiOff, Clock, LogIn, LogOut as LogOutIcon, Shield
} from 'lucide-react';

interface AppLayoutProps {
  children: ReactNode;
  activeTab: string;
  onNavigate: (screen: string) => void;
  onOpenXAssist: () => void;
  isDarkMode: boolean;
  onToggleTheme: () => void;
  companyName?: string;
  companyLogo?: string | null;
  isVoiceListening?: boolean;
  onToggleVoiceListening?: () => void;
  user?: {
    name: string;
    role: string;
    isOnline: boolean;
    email?: string;
  };
  onOpenProfile?: () => void;
  onLogout?: () => void;
  isClockedIn?: boolean;
  onToggleTimeClock?: () => void;
  viewAsField?: boolean;
  onReturnToCommandCenter?: () => void;
}

export function AppLayout({
  children,
  activeTab,
  onNavigate,
  onOpenXAssist,
  isDarkMode,
  onToggleTheme,
  companyName,
  companyLogo,
  isVoiceListening,
  onToggleVoiceListening,
  user,
  onOpenProfile,
  onLogout,
  isClockedIn,
  onToggleTimeClock,
  viewAsField = false,
  onReturnToCommandCenter
}: AppLayoutProps) {
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const mainTabs = [
    { name: 'Dashboard', icon: Home, screen: 'dashboard', gradient: 'from-blue-600 to-blue-700', color: 'text-blue-600' },
    { name: 'Wells', icon: MapPin, screen: 'wells', gradient: 'from-emerald-600 to-green-700', color: 'text-emerald-600' },
    { name: 'Routes', icon: Navigation, screen: 'routes', gradient: 'from-cyan-600 to-teal-700', color: 'text-cyan-600' },
    { name: 'Tickets', icon: Ticket, screen: 'tickets', gradient: 'from-rose-600 to-pink-700', color: 'text-rose-600' },
    { name: 'Analytics', icon: BarChart3, screen: 'analytics', gradient: 'from-purple-600 to-violet-700', color: 'text-purple-600' },
    { name: 'More', icon: SettingsIcon, screen: 'more', gradient: 'from-orange-500 to-orange-600', color: 'text-orange-500' },
  ];

  const quickActions = [
    { label: 'Navigation', icon: Navigation, onClick: () => onNavigate('navigation'), color: 'from-orange-500 to-orange-600' },
    { label: 'Map', icon: MapIcon, onClick: () => onNavigate('map'), color: 'from-emerald-500 to-green-600' },
    { label: 'X-Assist', icon: Sparkles, onClick: onOpenXAssist, color: 'from-pink-500 to-rose-500', highlight: true },
    { label: 'Quick', icon: Zap, onClick: () => onNavigate('quickEntry'), color: 'from-blue-500 to-cyan-600' },
    {
      label: isClockedIn ? 'Clock Out' : 'Clock In',
      icon: isClockedIn ? LogOutIcon : LogIn,
      onClick: onToggleTimeClock || (() => { }),
      color: 'from-purple-500 to-violet-600',
      highlight: !isClockedIn
    },
  ];

  return (
    <div className={`min-h-screen flex flex-col ${isDarkMode
      ? 'bg-background'
      : 'bg-gray-50'
      }`}>
      {/* Header - Stacked Layout */}
      <div className={`
        ${isDarkMode ? 'bg-card/95 border-border' : 'bg-white/95 border-gray-200'}
        backdrop-blur-xl border-b sticky top-0 z-40 shadow-lg
      `}>
        {/* Row 1: Logo + User Info + Action Icons */}
        <div className="h-20 px-4 flex items-center justify-between border-b border-gray-200 dark:border-border">
          {/* Logo - Bigger and more prominent */}
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${isDarkMode ? 'bg-black border border-gray-800 shadow-sm' : 'bg-white border border-gray-200 shadow-sm'
              }`}>
              <img
                src={companyLogo || (isDarkMode ? "/transparent-white-logo.png" : "/oilwellx-logo.png")}
                alt="Company Logo"
                className="w-12 h-12 object-contain"
              />
            </div>

            {/* Company & User Info */}
            <div className="flex flex-col justify-center">
              <div className={`text-lg font-bold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {companyName || 'OilWellX'}
              </div>
              <div className="flex items-center gap-1.5 mt-0.5">
                {user?.isOnline ? (
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                ) : (
                  <div className="w-2 h-2 rounded-full bg-gray-400" />
                )}
                <span className={`text-xs font-medium ${user?.isOnline ? 'text-green-600' : 'text-gray-500'}`}>
                  {user?.isOnline ? 'Online' : 'Offline'}
                </span>
              </div>
            </div>
          </div>

          {/* Right Side: User Dropdown + Action Icons */}
          <div className="flex items-center gap-2">

            {/* Voice Toggle */}
            <div className="relative group">
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={onToggleVoiceListening}
                className={`
                  w-11 h-11 rounded-xl flex items-center justify-center
                  ${isVoiceListening
                    ? 'bg-green-100 border-green-300 hover:bg-green-200'
                    : (isDarkMode ? 'bg-slate-800 border border-slate-700 hover:bg-slate-700' : 'bg-gray-100 border border-gray-300 hover:bg-gray-200')
                  }
                  transition-colors
                  ${isVoiceListening ? 'ring-2 ring-green-500 ring-offset-2' : ''}
                `}
              >
                {isVoiceListening ? (
                  <Mic className="w-6 h-6 text-green-600" />
                ) : (
                  <div className="relative">
                    <Mic className={`w-6 h-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
                    <div className="absolute top-1/2 left-1/2 w-full h-0.5 bg-red-500 -translate-x-1/2 -translate-y-1/2 rotate-45" />
                  </div>
                )}
              </motion.button>

              {/* Voice Status Tooltip */}
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                whileHover={{ opacity: 1, y: 0 }}
                className="absolute top-full mt-2 right-0 w-40 bg-black/90 text-white text-[10px] p-2 rounded-lg text-center z-50 pointer-events-none backdrop-blur-sm shadow-xl border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                {isVoiceListening ? "Wake up with 'Hey X'" : 'Click to enable voice'}
                <div className="absolute -top-1 right-4 w-2 h-2 bg-black/90 rotate-45 border-t border-l border-white/10" />
              </motion.div>
            </div>

            {/* Notifications */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => onNavigate('alerts')}
              className={`
                w-11 h-11 rounded-xl flex items-center justify-center relative
                ${isDarkMode ? 'bg-slate-800 border border-slate-700 hover:bg-slate-700' : 'bg-gray-100 border border-gray-300 hover:bg-gray-200'}
                transition-colors
              `}
            >
              <Bell className={`w-6 h-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`} />
              <div className={`absolute top-2 right-2 w-2.5 h-2.5 bg-red-500 rounded-full border-2 ${isDarkMode ? 'border-slate-800' : 'border-white'}`} />
            </motion.button>

            {/* User Dropdown */}
            <div className="relative">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowUserDropdown(!showUserDropdown)}
                className="flex items-center gap-2 pl-1.5 pr-1.5 md:pl-2 md:pr-4 py-1.5 md:py-2 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white transition-all shadow-md"
              >
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div className="hidden md:flex flex-col items-start">
                  <span className="text-sm font-bold leading-tight">{user?.name || 'User'}</span>
                  <span className="text-xs opacity-90 leading-tight">{user?.role || 'Viewer'}</span>
                </div>
                <ChevronDown className={`w-4 h-4 md:w-5 md:h-5 transition-transform ${showUserDropdown ? 'rotate-180' : ''}`} />
              </motion.button>

              {/* Dropdown Menu */}
              {showUserDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className={`absolute right-0 top-full mt-2 w-64 rounded-xl shadow-xl border z-50 ${isDarkMode
                    ? 'bg-slate-800 border-slate-700'
                    : 'bg-white border-gray-200'
                    }`}
                >
                  <div className="p-4 border-b border-gray-200 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {user?.name || 'User'}
                        </div>
                        <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {user?.email || 'user@example.com'}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          {user?.isOnline ? (
                            <Wifi className="w-3 h-3 text-green-500" />
                          ) : (
                            <WifiOff className="w-3 h-3 text-gray-400" />
                          )}
                          <span className={`text-xs ${user?.isOnline ? 'text-green-600' : 'text-gray-500'}`}>
                            {user?.isOnline ? 'Online' : 'Offline'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Manager Portal - Prominent placement for managers viewing as field */}
                  {viewAsField && onReturnToCommandCenter && (
                    <div className="px-2 pb-2 border-b border-gray-200 dark:border-slate-700">
                      <button
                        onClick={() => {
                          onReturnToCommandCenter();
                          setShowUserDropdown(false);
                        }}
                        className="w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md"
                      >
                        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                          <Shield className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <span className="font-semibold text-sm">Return to Command Center</span>
                          <span className="text-xs opacity-80 block">Exit Field View</span>
                        </div>
                      </button>
                    </div>
                  )}

                  <div className="p-2">
                    {/* Theme Toggle */}
                    <button
                      onClick={() => {
                        onToggleTheme();
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${isDarkMode
                        ? 'text-gray-300 hover:bg-slate-700 hover:text-white'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                    >
                      {isDarkMode ? <Sun className="w-4 h-4 text-yellow-400" /> : <Moon className="w-4 h-4" />}
                      <span className="text-sm">{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>

                    <button
                      onClick={() => {
                        onOpenProfile?.();
                        setShowUserDropdown(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${isDarkMode
                        ? 'text-gray-300 hover:bg-slate-700 hover:text-white'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                    >
                      <Settings className="w-4 h-4" />
                      <span className="text-sm">Profile Settings</span>
                    </button>

                    <button
                      onClick={() => {
                        onLogout?.();
                        setShowUserDropdown(false);
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20`}
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="text-sm">Sign Out</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Navigation Tabs with CENTER ADD BUTTON */}
        <div className="px-2 py-2.5">
          <div className="flex items-center justify-center gap-1.5">
            {/* First 3 tabs */}
            {mainTabs.slice(0, 3).map((tab) => {
              const isActive = tab.screen === activeTab;
              return (
                <motion.button
                  key={tab.name}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onNavigate(tab.screen)}
                  className={`
                    flex-1 flex flex-col items-center gap-1.5 px-1.5 py-2.5 rounded-2xl transition-all
                    ${isActive
                      ? `bg-gradient-to-br ${tab.gradient} text-white shadow-xl`
                      : (isDarkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-slate-800/50' : `${tab.color} hover:bg-gray-100`)
                    }
                  `}
                >
                  <tab.icon className="w-6 h-6" strokeWidth={2.5} />
                  <span className={`text-[10px] leading-none ${isActive ? 'text-white font-bold' : ''}`}>{tab.name}</span>
                </motion.button>
              );
            })}

            {/* CENTER ADD BUTTON - Same size as other tabs */}
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => onNavigate('quickEntry')}
              className={`
                flex-1 flex flex-col items-center gap-1.5 px-1.5 py-2.5 rounded-2xl transition-all
                bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 text-white shadow-lg
              `}
            >
              <Plus className="w-6 h-6" strokeWidth={2.5} />
              <span className="text-[10px] leading-none text-white font-bold">Add</span>
            </motion.button>

            {/* Last 3 tabs */}
            {mainTabs.slice(3).map((tab) => {
              const isActive = tab.screen === activeTab;
              return (
                <motion.button
                  key={tab.name}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => onNavigate(tab.screen)}
                  className={`
                    flex-1 flex flex-col items-center gap-1.5 px-1.5 py-2.5 rounded-2xl transition-all
                    ${isActive
                      ? `bg-gradient-to-br ${tab.gradient} text-white shadow-xl`
                      : (isDarkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-slate-800/50' : `${tab.color} hover:bg-gray-100`)
                    }
                  `}
                >
                  <tab.icon className="w-6 h-6" strokeWidth={2.5} />
                  <span className={`text-[10px] leading-none ${isActive ? 'text-white font-bold' : ''}`}>{tab.name}</span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto pb-24">
        {children}
      </div>

      {/* Bottom Quick Actions Bar - Docked Design */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="fixed bottom-0 left-0 right-0 z-50 pb-safe"
      >
        <div className={`
          ${isDarkMode ? 'bg-slate-800/95 border-t border-slate-700' : 'bg-white/95 border-t border-gray-200'}
          backdrop-blur-xl p-2 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] w-full
        `}>
          <div className="grid grid-cols-5 gap-1 max-w-md mx-auto items-end pb-1">
            {quickActions.map((action, index) => {
              const isXAssist = action.label === 'X-Assist';
              const containerSize = isXAssist ? 'w-14 h-14 mb-1' : 'w-12 h-12';
              const iconSize = isXAssist ? 'w-7 h-7' : 'w-6 h-6';

              return (
                <motion.button
                  key={action.label}
                  initial={{ opacity: 0, scale: 0.5, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{
                    delay: 0.7 + index * 0.05,
                    type: "spring",
                    stiffness: 400,
                    damping: 20
                  }}
                  whileHover={{ scale: 1.1, y: -8 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={action.onClick}
                  className="flex flex-col items-center gap-1.5 relative"
                >
                  <motion.div
                    className={`${containerSize} rounded-2xl bg-gradient-to-br ${action.color} flex items-center justify-center relative shadow-lg`}
                    animate={action.highlight ? {
                      boxShadow: [
                        '0 4px 14px rgba(236, 72, 153, 0.3)',
                        '0 8px 24px rgba(236, 72, 153, 0.5)',
                        '0 4px 14px rgba(236, 72, 153, 0.3)',
                      ]
                    } : {}}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <action.icon className={`${iconSize} text-white relative z-10`} />

                    {action.highlight && (
                      <motion.div
                        className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-yellow-400 rounded-full border-2 ${isDarkMode ? 'border-slate-800' : 'border-white'}`}
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      />
                    )}
                  </motion.div>
                  <span className={`text-[10px] font-medium leading-none ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{action.label}</span>
                </motion.button>
              );
            })}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
